import { useState, useRef, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../../component/b2b/navBar/b2bNavBar'
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import { useForm } from 'react-hook-form';
import { IoMdArrowRoundBack } from "react-icons/io";
import {
    FaCalculator, FaUser, FaShoppingCart, FaPlus, FaBox, FaTrash, FaShieldAlt,
    FaWarehouse, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaHashtag, FaCity,
    FaFlag, FaRupeeSign, FaWallet, FaClipboardList, FaWeightHanging, FaBoxOpen,
    FaRulerCombined, FaFileInvoice, FaCloudUploadAlt, FaFileAlt, FaSearch
} from 'react-icons/fa';
import { useMyContext } from '../../../context/MyContext';
import '../../../css/custom-scrollbar.css';
import B2BViewRateForword from '../../../component/b2b/models/priceEstimatorModelB2b';
import { Base_Url, b2bAddShipment, getB2bpIncode, b2bWarehouseListAll, } from '../../../config/config';
import { toast } from 'react-toastify';
import Spinner from '../../../component/spinner/spinner';
import axios from 'axios';
import B2BUploadBulk from '../../../component/b2b/models/uploadBulkModel';
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const schema = z.object({
    warehouse: z.string().optional(),
    firstName: z.string().min(1, "First Name is required").regex(/^[a-zA-Z\s]+$/, "First Name must only contain alphabetic characters"),
    lastName: z.string().min(1, "Last Name is required").regex(/^[a-zA-Z\s]+$/, "Last Name must only contain alphabetic characters"),
    email: z.string().email("Invalid email format").optional().or(z.literal("")),
    mobile: z.string()
        .length(10, "Mobile number must be exactly 10 digits")
        .regex(/^\d+$/, "Mobile number must be numeric"),
    address: z
        .string()
        .trim()
        .min(10, "Full Address must be at least 10 characters")
        .regex(/^[^#\\%&;]*$/, "Address contains invalid characters (# \\ % & ; not allowed)")
        .refine((val) => val.trim().length >= 10, {
            message: "Address cannot be only spaces",
        }),
    pincode: z.string()
        .length(6, "Pincode should be exactly 6 characters")
        .regex(/^\d+$/, "Pincode must be numeric"),
    city: z.string().min(1, "City is required").regex(/^[a-zA-Z\s]+$/, "City must only contain alphabetic characters"),
    state: z.string().min(1, "State is required").regex(/^[a-zA-Z\s]+$/, "State must only contain alphabetic characters"),
    orderId: z.string().min(1, "Order ID is required"),
    invoiceAmount: z.string()
        .min(1, "Invoice Amount is required")
        .regex(/^\d+(\.\d+)?$/, "Invoice Amount must be numeric"),
    invoiceNumber: z.string()
        .min(1, "Invoice number is required")
        .regex(/^[A-Za-z0-9\/-]*$/, "Invoice number must only contain letters, numbers, / or -"),
    eWaybillNo: z.string()
        .max(12, "E-Waybill number must be at most 12 characters")
        .optional(),
    paymentMode: z.string().min(1, "Payment Mode is required"),
    productDes: z.string().min(1, "Product Description is required"),
    codAmount: z.string().optional(),
    insurence: z.string().optional(),
    total_weight: z.string()
        .min(1, "Total Weight is required")
        .regex(/^\d*\.?\d+$/, "Total Weight must be a positive number and can be a float"),
    length: z.string()
        .regex(/^\d*\.?\d+$/, "Length must be a positive number and can be a float")
        .optional(),
    width: z.string()
        .regex(/^\d*\.?\d+$/, "Width must be a positive number and can be a float")
        .optional(),
    height: z.string()
        .regex(/^\d*\.?\d+$/, "Height must be a positive number and can be a float")
        .optional(),
}).refine(
    (data) => {
        const amount = parseFloat(data.invoiceAmount);
        if (amount > 50000) {
            return !!data.eWaybillNo && data.eWaybillNo.trim() !== "";
        }
        return true;
    },
    {
        message: "E-Waybill number is required when Invoice Amount is greater than 50000",
        path: ["eWaybillNo"],
    }
);

// ---------- visual-only helper components ----------
const errorText = (msg) => (
    <p className="text-red-400 text-[11px] mt-1.5 font-medium flex items-center gap-1">
        <span className="w-1 h-1 rounded-full bg-red-400 inline-block" />
        {msg}
    </p>
);

const Field = ({ icon: Icon, label, error, children, htmlFor, isDarkMode }) => {
    const labelBase = `block text-[11px] font-medium uppercase tracking-wider mb-1.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`;
    return (
        <div>
            {label && <label htmlFor={htmlFor} className={labelBase}>{label}</label>}
            <div className="relative group">
                {Icon && (
                    <Icon
                        size={13}
                        className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                            ${isDarkMode ? "text-slate-500 group-focus-within:text-purple-400" : "text-slate-400 group-focus-within:text-purple-500"}`}
                    />
                )}
                {children}
            </div>
            {error && errorText(error)}
        </div>
    );
};

const SectionCard = ({ number, icon: Icon, title, subtitle, children, className = "", isDarkMode }) => (
    <div
        className={`relative overflow-hidden rounded-2xl border p-6 backdrop-blur-xl transition-colors
            ${isDarkMode
                ? "bg-white/[0.035] border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
                : "bg-white border-slate-200/70 shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
            } ${className}`}
    >
        <div className="relative flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white shadow-lg shadow-purple-500/30 shrink-0">
                    <Icon size={15} />
                </span>
                <div>
                    <h2 className={`text-sm font-bold tracking-wide ${isDarkMode ? "text-white" : "text-slate-800"}`}>
                        {title}
                    </h2>
                    {subtitle && (
                        <p className={`text-[11px] mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        </div>
        <div className="relative">{children}</div>
    </div>
);

// Searchable Warehouse Dropdown Component
const SearchableWarehouseSelect = ({ 
    value, 
    onChange, 
    options, 
    isDarkMode, 
    error,
    label,
    placeholder = "Search warehouse by name or ID..."
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Find selected option label
    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption ? selectedOption.label : '';

    // Filter options based on search term
    const filteredOptions = options.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        onChange(option.value);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleInputClick = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
        }
    };

    const inputBase = `w-full pl-11 pr-2 py-1 text-sm rounded-md border transition-all duration-200 outline-none
        placeholder:text-slate-300 placeholder:text-sm
        ${isDarkMode
            ? "bg-white/[0.04] border-white/10 text-white focus:border-purple-400/70 focus:ring-4 focus:ring-purple-400/10 focus:bg-white/[0.06]"
            : "bg-slate-50 border-slate-200 text-slate-800 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 focus:bg-white"
        }`;

    return (
        <div className="relative" ref={dropdownRef}>
            <div className="relative group">
                <FaWarehouse
                    size={13}
                    className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                        ${isDarkMode ? "text-slate-500 group-focus-within:text-purple-400" : "text-slate-400 group-focus-within:text-purple-500"}`}
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={isOpen ? searchTerm : displayValue}
                    onChange={(e) => {
                        if (isOpen) {
                            setSearchTerm(e.target.value);
                            // If search term is cleared, don't auto-select
                            if (e.target.value === '') {
                                // Optionally clear selection
                            }
                        }
                    }}
                    onFocus={() => setIsOpen(true)}
                    onClick={handleInputClick}
                    placeholder={placeholder}
                    className={inputBase}
                />
                <button
                    type="button"
                    onClick={handleInputClick}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                    <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div 
                    className={`absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-md border shadow-lg
                        ${isDarkMode 
                            ? "bg-[#0b0f1a] border-white/10" 
                            : "bg-white border-slate-200"
                        }`}
                >
                    {filteredOptions.length === 0 ? (
                        <div className={`px-4 py-3 text-sm ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>
                            No warehouses found
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => handleSelect(option)}
                                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors flex items-center justify-between
                                    ${option.value === value 
                                        ? (isDarkMode ? "bg-purple-500/20 text-purple-300" : "bg-purple-50 text-purple-700") 
                                        : (isDarkMode ? "hover:bg-white/[0.06] text-white" : "hover:bg-slate-50 text-slate-700")
                                    }`}
                            >
                                <div className="flex flex-col">
                                    <span className="font-medium">{option.label}</span>
                                    <span className={`text-xs ${isDarkMode ? "text-slate-400" : "text-gray-400"}`}>
                                        ID: {option.value}
                                    </span>
                                </div>
                                {option.pincode && (
                                    <span className={`text-xs ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>
                                        PIN: {option.pincode}
                                    </span>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
            {error && errorText(error)}
        </div>
    );
};

export default function B2BForwordShipment() {
    const [invoiceFileName, setInvoiceFileName] = useState('');
    const [secondaryFile, setSecondaryFile] = useState('');
    const { isDarkMode, isToggleSidebar, selectedCft } = useMyContext()
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [uploadBulkPopupOpen, setUploadBulkPopupOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tableData, setTableData] = useState(null);
    const Navigation = useNavigate();
    const [wareHouseList, setwareHouseList] = useState([])
    const [wareHouseId, setwareHouseId] = useState('')
    const [wareHousePincode, setwareHousePincode] = useState('');
    const [reqBody, setReqBody] = useState([]);
    const [totalBoxes, setTotalBoxes] = useState(0);
    const [selectedUnit, setSelectedUnit] = useState('cm');
    const [boxSets, setBoxSets] = useState([]);
    const [remainingBoxes, setRemainingBoxes] = useState(0);
    const [file, setFile] = useState(null);
    const [sec_file, set_Sec_File] = useState(null);
    const [lrnOption, setLrnOption] = useState('auto');
    const [manualLrn, setManualLrn] = useState('');
    const [phoneResults, setPhoneResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingPhone, setLoadingPhone] = useState(false);
    const [searchText, setSearchText] = useState("");

    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    const { register, handleSubmit, formState: { errors }, reset, trigger, watch, setValue } = useForm({
        resolver: zodResolver(schema),
    });

    const handleViewClick = () => {
        setIsPopupOpen(true);
    };
    const handleClose = () => {
        setIsPopupOpen(false);
    };

    const UploadBulkViewClick = () => {
        setUploadBulkPopupOpen(true);
    }

    const handleCloseUploadBulk = () => {
        setUploadBulkPopupOpen(false);
    };

    const pincode = watch("pincode");

    useEffect(() => {
        if (pincode && pincode.length === 6) {
            fetchPincodeData(pincode);
        }
    }, [pincode]);

    const fetchPincodeData = async (pincode) => {
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const urlgetPincode = `${Base_Url}${getB2bpIncode}?pincode=${pincode}`;
        try {
            const response = await fetch(urlgetPincode, {
                method: 'GET',
                headers: {
                    'token': token,
                    'cus_id': cus_id,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (data && data.status === "SUCCESS") {
                let { city, state } = data.data;
                city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
                state = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
                setValue("city", city);
                setValue("state", state);
            } else if (data && data.message === "Unauthorized access - Invalid token") {
                toast.error(data.message + " Please login Again");
            } else if (data && data.status === "FAIL") {
                toast.error("Something Went Wrong Please Try Again With Another Pincode");
            } else {
                toast.error("Failed to fetch city and state data");
            }
        } catch (error) {
            console.error('Error fetching pincode data:', error);
            toast.error("Error fetching pincode data");
        }
    };

    const paymentMode = watch("paymentMode");

    useEffect(() => {
        if (paymentMode === "prepaid") {
            setValue("codAmount", "0");
        } else if (paymentMode === "cod") {
            setValue("codAmount", "");
        }
    }, [paymentMode, setValue]);

    const handleInputChange = (event) => {
        const { value, id } = event.target;
        if (['pincode', 'mobile', 'boxQty'].includes(id)) {
            const filteredValue = value.replace(/[^0-9]/g, '');
            event.target.value = filteredValue;
        }
        if (['firstName', 'lastName', 'city', 'state'].includes(id)) {
            const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
            event.target.value = filteredValue;
        }
    };

    useEffect(() => {
        fetchWareHouseList()
    }, [])

    const fetchWareHouseList = async () => {
        const token = localStorage.getItem('token');
        const cusid = localStorage.getItem('cus_id');
        const headers = new Headers({
            'token': token,
            'cus_id': cusid,
            'Content-Type': 'application/json'
        });

        try {
            const response = await fetch(Base_Url + b2bWarehouseListAll, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const transformedData = data.data.map(warehouse => ({
                value: warehouse.warehouse_id,
                label: warehouse.name,
                pincode: warehouse.pincode
            }));
            setwareHouseList(transformedData)
        } catch (error) {
            console.error('Error:', error);
        }
    }

    const handleNaviagtion = () => {
        Navigation("/b2b/dashboard");
    }

    const onSubmit = async (data) => {
        setIsSubmitting(true);

        const invoiceAmount = parseFloat(data.invoiceAmount) || 0;

        if (!invoiceFileName) {
            toast.error("Invoice document is mandatory.");
            setIsSubmitting(false);
            return;
        }

        if (invoiceAmount > 50000 && !secondaryFile) {
            toast.error("Secondary document is mandatory for amounts greater than 50,000.");
            setIsSubmitting(false);
            return;
        }

        if (boxSets && boxSets.length > 0) {
            const invalidBoxSet = boxSets.find(box =>
                !box.quantity || !box.length || !box.width || !box.height ||
                box.quantity <= 0 || box.length <= 0 || box.width <= 0 || box.height <= 0
            );

            if (invalidBoxSet) {
                toast.error("Please fill all box set dimensions with positive values");
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const formData = new FormData();

            formData.append('warehouse_id', wareHouseId || '');
            formData.append('type', selectedCft || '');
            formData.append('total_boxes', totalBoxes || '');
            formData.append('payment_mode', data.paymentMode || '');
            formData.append('cod_amount', data.codAmount || '0');
            formData.append('total_weight', data.total_weight || '');

            const dropoffLocation = {
                consignee_name: `${data.firstName} ${data.lastName}`,
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                zip: data.pincode || '',
                phone: data.mobile || '',
                email: data.email || '',
            };
            formData.append('dropoff_location', JSON.stringify(dropoffLocation));

            const invoices = [{
                ewaybill: data.eWaybillNo,
                inv_num: data.invoiceNumber,
                inv_amt: data.invoiceAmount,
                inv_qr_code: ""
            }];
            formData.append('invoices', JSON.stringify(invoices));

            const shipmentDetails = [{
                order_id: data.orderId || '',
                box_count: totalBoxes || 1,
                description: data.productDes || '',
                weight: parseFloat(data.total_weight) || 0,
                waybills: [],
                master: "False"
            }];
            formData.append('shipment_details', JSON.stringify(shipmentDetails));

            const dimensions = boxSets && boxSets.length > 0
                ? boxSets.map(boxSet => ({
                    box_count: parseInt(boxSet.quantity) || 1,
                    length: parseFloat(boxSet.length) || 0,
                    width: parseFloat(boxSet.width) || 0,
                    height: parseFloat(boxSet.height) || 0
                }))
                : [{
                    box_count: 1,
                    length: parseFloat(data.length) || 0,
                    width: parseFloat(data.width) || 0,
                    height: parseFloat(data.height) || 0
                }];
            formData.append('dimensions', JSON.stringify(dimensions));

            const docData = [{
                doc_type: "INVOICE_COPY",
                doc_meta: {
                    invoice_num: [data.orderId || '']
                }
            }];
            formData.append('doc_data', JSON.stringify(docData));

            if (file) formData.append('doc_file', file);
            if (sec_file) formData.append('sec_doc', sec_file);

            const billingAddress = {
                name: `${data.firstName} ${data.lastName}`,
                company: "pickupxpress",
                consignor: data.firstName || '',
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                pin: data.pincode || '',
                phone: data.mobile || '',
                pan_number: "ABCDE1234F",
                gst_number: "09AAACH7409R1ZZ"
            };
            formData.append('billing_address', JSON.stringify(billingAddress));

            formData.append('insurence', data.insurence === 'carrierRisk' ? 'Carrier' : 'Owner');
            formData.append('fm_pickup', 'True');
            formData.append('retailer_id', '2');

            if (lrnOption === 'manual' && manualLrn) {
                formData.append('lrn', manualLrn);
            }

            const response = await axios.post(Base_Url + b2bAddShipment, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                setTimeout(() => toast.success("Shipment added successfully ....!"), 1000);
                setTableData(response.data);
                setIsSubmitting(false);
                handleNaviagtion();
            } else {
                toast.error(response.data.message);
                setIsSubmitting(false);
            }
        } catch (error) {
            if (error.response) {
                const { data } = error.response;
                if (data.messages && typeof data.messages === 'object') {
                    Object.values(data.messages).forEach((msg) => toast.error(msg));
                } else if (data.message) {
                    toast.error(data.message);
                }
                setIsSubmitting(false);
            } else if (error.request) {
                toast.error("No response from server. Please check your network.");
                setIsSubmitting(false);
            } else {
                toast.error("An unexpected error occurred.");
                setIsSubmitting(false);
            }
        }
    };

    const onErrors = (errors) => {
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey && errors[firstErrorKey]) {
            toast.error(errors[firstErrorKey].message);
        }
    };

    const handleTotalBoxesChange = (e) => {
        const value = parseInt(e.target.value) || 0;
        setTotalBoxes(value);
        setRemainingBoxes(value);
        setBoxSets([]);
    };

    const handleAddBoxSet = () => {
        if (remainingBoxes > 0) {
            setBoxSets([...boxSets, { quantity: remainingBoxes, length: '', width: '', height: '' }]);
            setRemainingBoxes(0);
        }
    };

    const handleBoxSetChange = (index, field, value) => {
        const newBoxSets = [...boxSets];
        const updatedBoxSet = { ...newBoxSets[index] };

        if (field === 'quantity') {
            const newQuantity = parseInt(value) || 0;
            const otherBoxesQuantity = boxSets.reduce((sum, box, i) =>
                i !== index ? sum + (parseInt(box.quantity) || 0) : sum, 0
            );

            if (newQuantity + otherBoxesQuantity <= totalBoxes) {
                updatedBoxSet.quantity = newQuantity;
                newBoxSets[index] = updatedBoxSet;
                setBoxSets(newBoxSets);
                setRemainingBoxes(totalBoxes - (newQuantity + otherBoxesQuantity));
            }
        } else {
            updatedBoxSet[field] = value;
            newBoxSets[index] = updatedBoxSet;
            setBoxSets(newBoxSets);
        }
    };

    const handleDeleteBoxSet = (index) => {
        const deletedQuantity = boxSets[index].quantity || 0;
        const newBoxSets = boxSets.filter((_, i) => i !== index);
        setBoxSets(newBoxSets);
        const totalUsedBoxes = newBoxSets.reduce((sum, box) => sum + (box.quantity || 0), 0);
        setRemainingBoxes(totalBoxes - totalUsedBoxes);
    };

    const fetchByPhone = async (phone) => {
        try {
            setLoadingPhone(true);
            const res = await fetch(
                `https://api.pickupxpress.com/B2B/shipment/phonefilter?phone=${phone}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        cus_id: localStorage.getItem("cus_id"),
                        token: localStorage.getItem("token"),
                    },
                }
            );

            const json = await res.json();
            if (json.status === "SUCCESS") {
                setPhoneResults(json.data || []);
                setShowDropdown(true);
            } else {
                setPhoneResults([]);
                setShowDropdown(false);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingPhone(false);
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            const inputId = event.target.id;
            if (inputId === 'invoiceDocs') {
                setFile(selectedFile);
                setInvoiceFileName(selectedFile.name);
            } else if (inputId === 'secondaryDocs') {
                set_Sec_File(selectedFile);
                setSecondaryFile(selectedFile.name);
            }
        }
    };

    const handleSelectCustomer = (item) => {
        const [firstName, ...lastName] = item.consignee_name.split(" ");
        setValue("firstName", firstName || "");
        setValue("lastName", lastName.join(" ") || "");
        setValue("email", item.email || "");
        setValue("address", item.address || "");
        setValue("city", item.city || "");
        setValue("state", item.state || "");
        setValue("pincode", item.zip || "");
        setValue("mobile", item.phone || "");
        setShowDropdown(false);
    };

    // Handle warehouse selection from searchable dropdown
    const handleWarehouseSelect = (selectedValue) => {
        const selectedItem = wareHouseList.find((item) => item.value === selectedValue);
        if (selectedItem) {
            setwareHouseId(selectedItem.value);
            setwareHousePincode(selectedItem.pincode);
            setValue("warehouse", selectedValue);
            trigger("warehouse");
        }
    };

    const inputBase = `w-full pl-11 pr-2 py-1 text-sm rounded-md border transition-all duration-200 outline-none
        placeholder:text-slate-300 placeholder:text-sm
        ${isDarkMode
            ? "bg-white/[0.04] border-white/10 text-white focus:border-purple-400/70 focus:ring-4 focus:ring-purple-400/10 focus:bg-white/[0.06]"
            : "bg-slate-50 border-slate-200 text-slate-800 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 focus:bg-white"
        }`;

    const inputPlain = `w-full px-4 py-3 text-sm rounded-xl border transition-all duration-200 outline-none
        placeholder:text-slate-400
        ${isDarkMode
            ? "bg-white/[0.04] border-white/10 text-white focus:border-purple-400/70 focus:ring-4 focus:ring-purple-400/10 focus:bg-white/[0.06]"
            : "bg-slate-50 border-slate-200 text-slate-800 focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 focus:bg-white"
        }`;

    const labelBase = `block text-[11px] font-medium uppercase tracking-wider mb-1.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`;

    return (
        <>
            <div className={`h-screen flex flex-col overflow-hidden relative ${isDarkMode ? 'dark:bg-[#080b14]' : 'bg-slate-50'}`}>
                <div className="z-10">
                    <NavBar />
                </div>
                <div className="flex flex-1 overflow-hidden relative">
                    {isToggleSidebar && (
                        <div
                            className={`fixed z-20 h-full w-64 md:relative duration-300 mt-4 md:w-64 md:z-20 ${isDarkMode ? "bg-[#0b0f1a] text-white" : "bg-gray-200 md:bg-transparent"}`}
                            style={
                                window.innerWidth < 768
                                    ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                                    : {}
                            } >
                            <Sidebar />
                        </div>
                    )}
                    <div
                        className={`flex-1 overflow-auto relative transition-all duration-300 custom-scrollbar ${isToggleSidebar ? 'md:ml-0' : 'md:ml-0'} ${isDarkMode ? "text-white" : "bg-gray-100"}`}
                    >
                        {isDarkMode && (
                            <>
                                <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full bg-purple-500/10 blur-[100px]" />
                                <div className="pointer-events-none absolute top-1/3 -right-24 w-96 h-96 rounded-full bg-fuchsia-400/10 blur-[110px]" />
                            </>
                        )}

                        <div className="container overflow-x-hidden mx-auto pl-6 pr-6 pt-6 pb-8 relative">

                            {/* PAGE HEADER */}
                            <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                                <div>
                                    <p className={`text-[11px] font-bold uppercase tracking-[0.2em] mb-1 ${isDarkMode ? "text-white bg-purple-600" : "text-white bg-purple-500"} inline-block px-3 py-1.5 rounded-lg`}>
                                        B2B · New Booking
                                    </p>
                                    <h1 className={`text-2xl md:text-[28px] font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r ${isDarkMode ? "from-white to-slate-300" : "from-slate-900 to-slate-600"}`}>
                                        Create Forward Shipment
                                    </h1>
                                    <p className={`text-[12px] mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                        Fill in customer, order, box and invoice details to generate a new B2B shipment.
                                    </p>
                                </div>

                                <div className="flex flex-col md:flex-row gap-3 md:shrink-0">
                                    <button onClick={handleNaviagtion}
                                        className="w-full md:w-auto px-5 bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white py-3 rounded-xl flex items-center justify-center font-semibold text-sm shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200">
                                        <IoMdArrowRoundBack className="mr-2" />
                                        Create Forward Order
                                    </button>
                                    <button
                                        className={`w-full md:w-auto px-5 py-3 rounded-xl flex items-center justify-center font-semibold text-sm border transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0
                                            ${isDarkMode
                                                ? "bg-white/[0.05] border-white/10 text-white hover:bg-white/[0.09]"
                                                : "bg-white border-slate-200 text-slate-700 hover:border-purple-300 hover:text-purple-600 shadow-sm"
                                            }`}
                                        onClick={UploadBulkViewClick}
                                    >
                                        <FaBox className="mr-2" />
                                        Upload Bulk
                                    </button>
                                </div>
                            </div>

                            <B2BUploadBulk
                                isopen={uploadBulkPopupOpen}
                                onClose={handleCloseUploadBulk}
                            />
                            <form onSubmit={handleSubmit(onSubmit, onErrors)}>
                                {/* ================= CUSTOMER DETAILS ================= */}
                                <SectionCard number="01" icon={FaUser} title="Customer Details" subtitle="Who is this shipment going to" className="mb-5" isDarkMode={isDarkMode}>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Row 1: Warehouse (Searchable), First Name, Last Name */}
                                        <div>
                                            <label className={labelBase}>Warehouse</label>
                                            <SearchableWarehouseSelect
                                                value={wareHouseId}
                                                onChange={handleWarehouseSelect}
                                                options={wareHouseList}
                                                isDarkMode={isDarkMode}
                                                error={errors.warehouse?.message}
                                                placeholder="Search warehouse by name or ID..."
                                            />
                                        </div>

                                        <Field icon={FaUser} label="First Name" htmlFor="firstName" error={errors.firstName?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="firstName"
                                                {...register("firstName")}
                                                className={inputBase}
                                                type="text"
                                                onChange={handleInputChange}
                                                placeholder="Enter first name"
                                            />
                                        </Field>

                                        <Field icon={FaUser} label="Last Name" htmlFor="lastName" error={errors.lastName?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="lastName"
                                                {...register("lastName")}
                                                className={inputBase}
                                                type="text"
                                                onChange={handleInputChange}
                                                placeholder="Last name"
                                            />
                                        </Field>

                                        {/* Row 2: Email, Mobile */}
                                        <Field icon={FaEnvelope} label="Email" htmlFor="email" isDarkMode={isDarkMode}>
                                            <input
                                                id="email"
                                                {...register("email")}
                                                className={inputBase}
                                                type="email"
                                                placeholder="Enter email"
                                            />
                                        </Field>

                                        <div className="relative">
                                            <label htmlFor="mobile" className={labelBase}>Mobile No.</label>
                                            <div className="relative group">
                                                <FaPhoneAlt
                                                    size={13}
                                                    className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                                                        ${isDarkMode ? "text-slate-500 group-focus-within:text-purple-400" : "text-slate-400 group-focus-within:text-purple-500"}`}
                                                />
                                                <input
                                                    id="mobile"
                                                    type="tel"
                                                    maxLength="10"
                                                    placeholder="Enter 10 digit mobile no."
                                                    {...register("mobile")}
                                                    className={inputBase}
                                                    onChange={(e) => {
                                                        const phone = e.target.value.replace(/\D/g, "");
                                                        setValue("mobile", phone);
                                                        if (phone.length === 10) {
                                                            fetchByPhone(phone);
                                                        } else {
                                                            setShowDropdown(false);
                                                        }
                                                    }}
                                                />
                                            </div>
                                            {errors.mobile && errorText(errors.mobile.message)}
                                            {showDropdown && (
                                                <div
                                                    className={`absolute z-50 w-[400px] max-h-48 overflow-y-auto rounded-xl border backdrop-blur-xl mt-1
                                                        ${isDarkMode ? "bg-[#0b0f1a]/95 border-white/10 shadow-2xl" : "bg-white border-slate-200 shadow-lg"}`}
                                                    onWheel={(e) => e.stopPropagation()}
                                                >
                                                    {phoneResults.map((item, index) => (
                                                        <div
                                                            key={index}
                                                            className={`p-2.5 text-xs cursor-pointer transition-colors ${isDarkMode ? "hover:bg-white/[0.06]" : "hover:bg-purple-50"}`}
                                                            onClick={() => handleSelectCustomer(item)}
                                                        >
                                                            <div className={`font-semibold ${isDarkMode ? "text-white" : "text-slate-800"}`}>{item.consignee_name}</div>
                                                            <div className={isDarkMode ? "text-slate-400" : "text-gray-500"}>
                                                                {item.address}, {item.city} - {item.zip}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* LRN Option */}
                                        <div>
                                            <label className={labelBase}>LRN Option</label>
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center mt-2 gap-3">
                                                    <div className="flex gap-3 flex-shrink-0">
                                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name="lrnOption"
                                                                value="auto"
                                                                checked={lrnOption === 'auto'}
                                                                onChange={() => setLrnOption('auto')}
                                                                className="accent-purple-500 w-3.5 h-3.5"
                                                            />
                                                            <span className={`text-xs font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>Auto</span>
                                                        </label>
                                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name="lrnOption"
                                                                value="manual"
                                                                checked={lrnOption === 'manual'}
                                                                onChange={() => setLrnOption('manual')}
                                                                className="accent-purple-500 w-3.5 h-3.5"
                                                            />
                                                            <span className={`text-xs font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>Manual</span>
                                                        </label>
                                                    </div>
                                                    {lrnOption === 'manual' && (
                                                        <div className="flex-1">
                                                            <input
                                                                type="text"
                                                                name="manualLrn"
                                                                placeholder="Enter LRN manually"
                                                                value={manualLrn}
                                                                onChange={(e) => setManualLrn(e.target.value)}
                                                                className={inputPlain}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.manualLrn && errorText(errors.manualLrn.message)}
                                            </div>
                                        </div>

                                        {/* Row 3: Address, Pincode, City */}
                                        <Field icon={FaMapMarkerAlt} label="Full Address" htmlFor="address" error={errors.address?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="address"
                                                {...register("address")}
                                                className={inputBase}
                                                type="text"
                                                placeholder="Enter full address"
                                            />
                                        </Field>

                                        <Field icon={FaHashtag} label="Pincode" htmlFor="pincode" error={errors.pincode?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="pincode"
                                                maxLength="6"
                                                {...register("pincode")}
                                                className={inputBase}
                                                type="text"
                                                placeholder="Enter pincode"
                                            />
                                        </Field>

                                        <Field icon={FaCity} label="City" htmlFor="city" error={errors.city?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="city"
                                                {...register("city")}
                                                className={inputBase}
                                                type="text"
                                                onChange={handleInputChange}
                                                placeholder="City"
                                            />
                                        </Field>

                                        {/* Row 4: State */}
                                        <Field icon={FaFlag} label="State" htmlFor="state" error={errors.state?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="state"
                                                {...register("state")}
                                                className={inputBase}
                                                type="text"
                                                onChange={handleInputChange}
                                                placeholder="State"
                                            />
                                        </Field>
                                    </div>
                                </SectionCard>

                                <div className="flex flex-col md:flex-row gap-5 mb-5">
                                    {/* ================= ORDER DETAILS ================= */}
                                    <SectionCard number="02" icon={FaShoppingCart} title="Order Details" subtitle="Payment & product info" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Field icon={FaHashtag} label="Order ID" htmlFor="orderId" error={errors.orderId?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="orderId"
                                                    {...register("orderId")}
                                                    className={inputBase}
                                                    type="text"
                                                    placeholder="Enter Order ID"
                                                />
                                            </Field>

                                            <Field icon={FaClipboardList} label="Product Description" htmlFor="productDes" error={errors.productDes?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="productDes"
                                                    {...register("productDes")}
                                                    className={inputBase}
                                                    type="text"
                                                    placeholder="Product Description"
                                                />
                                            </Field>

                                            <Field icon={FaWallet} label="Payment Mode" htmlFor="paymentMode" error={errors.paymentMode?.message} isDarkMode={isDarkMode}>
                                                <select
                                                    id="paymentMode"
                                                    {...register("paymentMode")}
                                                    className={inputBase}
                                                >
                                                    <option value="prepaid" className={isDarkMode ? "bg-[#0b0f1a]" : ""}>Prepaid</option>
                                                    <option value="cod" className={isDarkMode ? "bg-[#0b0f1a]" : ""}>COD</option>
                                                </select>
                                            </Field>

                                            <Field icon={FaRupeeSign} label="Amount" htmlFor="codAmount" error={errors.codAmount?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="codAmount"
                                                    {...register("codAmount")}
                                                    className={`${inputBase} disabled:opacity-40 disabled:cursor-not-allowed`}
                                                    type="text"
                                                    placeholder="Amount"
                                                    disabled={paymentMode !== "cod"}
                                                />
                                            </Field>
                                        </div>

                                        {/* Insurance Section */}
                                        <div className={`mt-4 p-4 rounded-xl border ${isDarkMode ? "bg-white/[0.03] border-white/10" : "bg-purple-50/60 border-purple-100"}`}>
                                            <h3 className={`text-sm font-semibold flex items-center gap-1.5 ${isDarkMode ? "text-purple-300" : "text-purple-700"}`}>
                                                <FaShieldAlt className="text-xs" />
                                                Insure your shipment
                                            </h3>
                                            <p className={`text-xs mt-1 ${isDarkMode ? "text-slate-400" : "text-gray-600"}`}>
                                                Are you sure you want to ship the item at your own risk?
                                            </p>
                                            <div className="mt-3 space-y-2">
                                                <label className="flex items-center space-x-2 cursor-pointer text-xs">
                                                    <input
                                                        type="radio"
                                                        {...register("insurance")}
                                                        value="ownerRisk"
                                                        className="accent-purple-500 w-3.5 h-3.5"
                                                    />
                                                    <span className={`font-medium ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                                                        Yes,(The charges will be applied as per agreement)
                                                    </span>
                                                </label>
                                                <label className="flex items-center space-x-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        {...register("insurance")}
                                                        value="carrierRisk"
                                                        className="accent-purple-500 w-3.5 h-3.5"
                                                    />
                                                    <span className={`font-medium text-xs ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                                                        No,<span className={isDarkMode ? "text-slate-400" : "text-slate-500"}> (The nominal charges will be applied)</span>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    </SectionCard>

                                    {/* ================= BOX DETAILS ================= */}
                                    <SectionCard number="03" icon={FaBox} title="Box Details" subtitle="Total boxes, weight & dimensions" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="mb-4">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <Field icon={FaBoxOpen} label="Total Boxes" isDarkMode={isDarkMode}>
                                                    <input
                                                        type="number"
                                                        onChange={handleTotalBoxesChange}
                                                        className={inputBase}
                                                        placeholder="Enter total boxes"
                                                    />
                                                </Field>
                                                <Field icon={FaWeightHanging} label="Total Weight (kg)" isDarkMode={isDarkMode}>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        {...register("total_weight")}
                                                        className={inputBase}
                                                        placeholder="Enter total weight"
                                                    />
                                                </Field>
                                                <Field>
                                                    <label className={labelBase}>Unit</label>
                                                    <select
                                                        className="border border-gray-300 rounded-lg px-1 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full"
                                                    >
                                                        <option value="cm">Centimeters (cm)</option>
                                                    </select>
                                                </Field>
                                            </div>
                                        </div>

                                        {boxSets.map((boxSet, index) => (
                                            <div key={index} className={`mb-3 p-3 rounded-xl border ${isDarkMode ? "bg-white/[0.03] border-white/10" : "bg-slate-50 border-slate-200"}`}>
                                                <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
                                                    <div>
                                                        <label className={labelBase}>Quantity</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={remainingBoxes + (boxSet.quantity || 0)}
                                                            value={boxSet.quantity || ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'quantity', e.target.value)}
                                                            className={`${inputPlain} !py-2 ${!boxSet.quantity || boxSet.quantity <= 0 ? '!border-red-500/70' : ''}`}
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className={labelBase}>Length({selectedUnit})</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0.01"
                                                            value={boxSet.length || ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'length', e.target.value)}
                                                            className={`${inputPlain} !py-2 ${!boxSet.length || parseFloat(boxSet.length) <= 0 ? '!border-red-500/70' : ''}`}
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className={labelBase}>Width({selectedUnit})</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0.01"
                                                            value={boxSet.width || ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'width', e.target.value)}
                                                            className={`${inputPlain} !py-2 ${!boxSet.width || parseFloat(boxSet.width) <= 0 ? '!border-red-500/70' : ''}`}
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className={labelBase}>Height({selectedUnit})</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0.01"
                                                            value={boxSet.height || ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'height', e.target.value)}
                                                            className={`${inputPlain} !py-2 ${!boxSet.height || parseFloat(boxSet.height) <= 0 ? '!border-red-500/70' : ''}`}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="flex items-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteBoxSet(index)}
                                                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "text-red-400 hover:bg-red-500/10" : "text-red-500 hover:bg-red-50"}`}
                                                            title="Delete box set"
                                                        >
                                                            <FaTrash size="14" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <div className="flex items-center justify-between">
                                            <button
                                                onClick={handleAddBoxSet}
                                                disabled={remainingBoxes === 0}
                                                className={`flex items-center px-4 py-2 text-xs font-semibold rounded-lg transition-all duration-200 ${remainingBoxes === 0
                                                        ? (isDarkMode ? 'bg-white/5 text-slate-500 cursor-not-allowed' : 'bg-gray-200 text-slate-400 cursor-not-allowed')
                                                        : 'bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white shadow-md shadow-purple-500/25 hover:-translate-y-0.5'
                                                    }`}
                                            >
                                                <FaPlus className="mr-1.5 text-xs" />
                                                Add Box Set
                                            </button>
                                            {remainingBoxes > 0 && (
                                                <span className={`text-xs font-medium ${isDarkMode ? "text-slate-400" : "text-gray-600"}`}>Remaining boxes: {remainingBoxes}</span>
                                            )}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="flex flex-col md:flex-row gap-5 mb-5">
                                    {/* ================= INVOICE DETAILS ================= */}
                                    <SectionCard number="04" icon={FaFileInvoice} title="Invoice Details" subtitle="Invoice number, e-waybill & amount" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Field icon={FaHashtag} label="Invoice Number" htmlFor="invoiceNumber" error={errors.invoiceNumber?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="invoiceNumber"
                                                    {...register("invoiceNumber")}
                                                    className={inputBase}
                                                    type="text"
                                                    placeholder="Enter Invoice Number"
                                                />
                                            </Field>
                                            <Field icon={FaHashtag} label="E-WaybillNo" htmlFor="eWaybillNo" error={errors.eWaybillNo?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="eWaybillNo"
                                                    {...register("eWaybillNo")}
                                                    className={inputBase}
                                                    type="text"
                                                    placeholder="E-WaybillNo"
                                                />
                                            </Field>
                                            <Field icon={FaRupeeSign} label="Amount" htmlFor="totalAmount" error={errors.totalAmount?.message} isDarkMode={isDarkMode}>
                                                <input
                                                    id="totalAmount"
                                                    {...register("invoiceAmount")}
                                                    className={inputBase}
                                                    type="text"
                                                    placeholder="Total Amount"
                                                />
                                            </Field>
                                        </div>
                                    </SectionCard>

                                    {/* ================= INVOICE DOCUMENTS ================= */}
                                    {/* <SectionCard number="05" icon={FaCloudUploadAlt} title="Invoice Documents" subtitle="Upload supporting files" className="flex-1" isDarkMode={isDarkMode}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="invoiceDocs" className={labelBase}>
                                                    Invoice Documents
                                                    <span className={`normal-case font-normal ml-1 ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>(PDF/PNG/JPG/JPEG)</span>
                                                </label>
                                                <div className={`relative border-2 border-dashed rounded-xl p-4 transition-colors
                                                    ${isDarkMode ? "border-white/15 hover:border-purple-400/50 bg-white/[0.02]" : "border-slate-300 hover:border-purple-400"}`}>
                                                    <input
                                                        id="invoiceDocs"
                                                        type="file"
                                                        accept=".pdf,.png,.jpg,.jpeg"
                                                        onChange={handleFileChange}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                    />
                                                    <div className="text-center flex flex-col items-center gap-1">
                                                        <FaCloudUploadAlt className={isDarkMode ? "text-slate-500" : "text-slate-400"} size={18} />
                                                        <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-gray-600"}`}>
                                                            Drop files here or click to upload
                                                        </p>
                                                    </div>
                                                </div>
                                                {invoiceFileName && (
                                                    <div className={`mt-2 px-3 py-2 rounded-lg flex items-center gap-2 ${isDarkMode ? "bg-purple-500/10 border border-purple-400/20" : "bg-purple-50 border border-purple-100"}`}>
                                                        <FaFileAlt className={isDarkMode ? "text-purple-300" : "text-purple-600"} size={12} />
                                                        <p className={`text-xs truncate ${isDarkMode ? "text-purple-200" : "text-purple-700"}`}>
                                                            {invoiceFileName}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="secondaryDocs" className={labelBase}>
                                                    Secondary Documents
                                                    <span className={`normal-case font-normal ml-1 ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>(PDF/PNG/JPG/JPEG)</span>
                                                </label>
                                                <div className={`relative border-2 border-dashed rounded-xl p-4 transition-colors
                                                    ${isDarkMode ? "border-white/15 hover:border-purple-400/50 bg-white/[0.02]" : "border-slate-300 hover:border-purple-400"}`}>
                                                    <input
                                                        id="secondaryDocs"
                                                        type="file"
                                                        onChange={handleFileChange}
                                                        accept=".pdf,.png,.jpg,.jpeg"
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                    />
                                                    <div className="text-center flex flex-col items-center gap-1">
                                                        <FaCloudUploadAlt className={isDarkMode ? "text-slate-500" : "text-slate-400"} size={18} />
                                                        <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-gray-600"}`}>
                                                            Drop files here or click to upload
                                                        </p>
                                                    </div>
                                                </div>
                                                {secondaryFile && (
                                                    <div className={`mt-2 px-3 py-2 rounded-lg flex items-center gap-2 ${isDarkMode ? "bg-purple-500/10 border border-purple-400/20" : "bg-purple-50 border border-purple-100"}`}>
                                                        <FaFileAlt className={isDarkMode ? "text-purple-300" : "text-purple-600"} size={12} />
                                                        <p className={`text-xs truncate ${isDarkMode ? "text-purple-200" : "text-purple-700"}`}>
                                                            {secondaryFile}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </SectionCard> */}
                                </div>

                                {/* SUBMIT */}
                                <div className="flex justify-center mt-2 mb-3">
                                    <button
                                        type="submit"
                                        className="group relative w-full md:w-[42%] overflow-hidden bg-gradient-to-r from-purple-500 via-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white py-4 font-bold text-[16px] rounded-xl flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                                    >
                                        <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                                        <span className="relative flex items-center gap-2">
                                            {isSubmitting ? <Spinner /> : 'Add order'}
                                            <FaCalculator size={14} />
                                        </span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                <B2BViewRateForword
                    isopen={isPopupOpen}
                    onClose={handleClose}
                    data={tableData}
                    reqBody={reqBody}
                />
            </div>
        </>
    )
}